import { ReactElement, ReactNode, useState } from 'react';
import { Tooltip, useTheme } from '@mui/material';
import type { TooltipProps } from '@mui/material/Tooltip';
import ErrorIcon from '@mui/icons-material/Error';

export interface FieldErrorTooltipProps {
    /** Message revealed inside the callout tooltip. */
    message?: ReactNode;
    /** Force the tooltip open (in addition to hover) to auto-surface a single-field error. */
    open?: boolean;
    /** Icon colour; defaults to the MUI error colour. */
    color?: string;
    placement?: TooltipProps['placement'];
    /** Custom hover target; defaults to the red ErrorIcon when omitted. */
    children?: ReactElement;
}

// Shared in-field error indicator: a red ErrorIcon that reveals `message` in a callout tooltip on
// hover or when `open` is forced. Visual mirror of the adornment TextboxInput renders so dropdown
// and date-picker cells match the text inputs 1:1.
export const FieldErrorTooltip = ({ message, open = false, color, placement = 'bottom', children }: FieldErrorTooltipProps) => {
    const theme = useTheme();
    // Keep the tooltip controlled at all times so hover keeps working even after `open` toggles
    // from true to false (a MUI Tooltip flipping controlled<->uncontrolled leaves hover broken).
    const [hovered, setHovered] = useState(false);
    return (
        <Tooltip
            arrow
            placement={placement}
            open={open || hovered}
            onOpen={() => setHovered(true)}
            onClose={() => setHovered(false)}
            title={message ?? ''}
            componentsProps={{
                tooltip: {
                    sx: (theme) => ({
                        backgroundColor: theme.palette.background.paper,
                        color: theme.palette.semantic.text.default,
                        border: `1px solid ${theme.palette.semantic.bd.subtle}`,
                        boxShadow: '0px 4px 12px rgba(0,0,0,0.08)',
                        fontSize: '14px',
                        padding: '8px 12px',
                        borderRadius: '8px'
                    })
                },
                arrow: {
                    sx: (theme) => ({
                        color: theme.palette.background.paper,
                        '&::before': { border: `1px solid ${theme.palette.semantic.bd.subtle}` }
                    })
                }
            }}
        >
            {children ?? <ErrorIcon sx={{ color: color ?? theme.palette.error.main, cursor: 'pointer' }} />}
        </Tooltip>
    );
};
